// Ömlesztett számla-PDF feltöltés (accountant/bulk-upload): közös hook + státusz panel.
// Fájlonként: hozzárendelés PDF nélküli bizonylathoz / új bizonylat / duplikátum /
// DÖNTÉS KELL (needs_review) / hiba. A bedobott fájlok SORBA állnak; a panel
// folyamatjelzőt, becsült hátralévő időt és megszakítás gombot ad.
//
// needs_review: ha nincs biztos egyezés, a backend nem hoz létre vakon új
// bizonylatot, hanem felkínálja a lehetséges meglévő bizonylatokat. A felhasználó
// itt dönt: 'Csatol ehhez' (PATCH /documents/{id}/pdf) / 'Mégis új bizonylat'
// (bulk-upload force_create) / 'Elvet'.
import { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { accountantApi, documentsApi } from '../services/api';
import { Badge, Spinner, Btn } from './UI';
import { huFmt } from '../utils';
import { usePostHog } from '@posthog/react';

const ACTION_BADGE = {
  attached:     ['success', 'Hozzárendelve'],
  created:      ['primary', 'Új bizonylat'],
  duplicate:    ['warning', 'Duplikátum'],
  needs_review: ['warning', 'Döntés kell'],
  resolving:    ['default', 'Mentés…'],
  skipped:      ['default', 'Elvetve'],
  failed:       ['danger',  'Hiba'],
  cancelled:    ['default', 'Megszakítva'],
  working:      ['default', 'Feldolgozás…'],
  pending:      ['default', 'Várakozik'],
};

// Ezek a státuszok "nyitva tartják" a panelt (nem zárul be magától) és nem
// számítanak késznek a folyamatjelzőn.
const OPEN_STATUSES = ['failed', 'cancelled', 'pending', 'working',
                       'needs_review', 'resolving'];

const DEFAULT_FILE_SECONDS = 35;   // első fájlig nincs mérés – tapasztalati érték

export function useBulkUpload(period) {
  const qc = useQueryClient();
  const posthog = usePostHog();
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);     // {name, status, label, candidates?, extracted?}
  const [notice, setNotice] = useState(null); // pl. "nem érkezett feldolgozható fájl"
  const queueRef = useRef([]);                // {file, idx}
  const runningRef = useRef(false);
  const countRef = useRef(0);
  const cancelRef = useRef(false);
  const abortRef = useRef(null);
  const durationsRef = useRef([]);            // mp / fájl – ETA-hoz
  const filesRef = useRef({});                // idx → File (resolve-hoz megőrizzük)

  const setItem = (idx, patch) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['accountant-check'] });
    qc.invalidateQueries({ queryKey: ['documents'] });
  };

  const runWorker = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setBusy(true);
    // Egyesével (AI rate limit miatt szekvenciálisan); a sor közben bővülhet
    while (queueRef.current.length) {
      if (cancelRef.current) break;
      const { file, idx } = queueRef.current.shift();
      setItem(idx, { status: 'working' });
      const t0 = Date.now();
      let patch = { status: 'failed', label: '' };
      try {
        abortRef.current = new AbortController();
        const r = await accountantApi.bulkUpload(period, file, abortRef.current.signal);
        if (r.action === 'attached')
          patch = { status: 'attached',
            label: `${r.matched_supplier || ''} ${r.matched_invoice_number || ''} (${r.accounting_period || '?'})` };
        else if (r.action === 'created')
          // számla: doc_id-s részletes címke; bér/adó/kivonat (univerzális router):
          // a reason-t mutatjuk (több tétel, nincs egyetlen doc_id)
          patch = { status: 'created',
            label: r.doc_id
              ? `${r.matched_supplier || '?'} ${r.matched_invoice_number || ''} → #${r.doc_id} (${r.accounting_period || 'nincs időszak!'})`
              : (r.reason || 'létrehozva') };
        else if (r.action === 'duplicate')
          patch = { status: 'duplicate',
            label: r.matched_invoice_number || r.matched_supplier
              ? `már létezik PDF-fel: ${r.matched_supplier || ''} ${r.matched_invoice_number || ''}`
              : (r.reason || 'már létezik') };
        else if (r.action === 'needs_review')
          patch = { status: 'needs_review', label: '',
            candidates: r.candidates || [], extracted: r.extracted || {} };
        else
          patch = { status: 'failed', label: r.reason || 'Ismeretlen hiba' };
        durationsRef.current.push((Date.now() - t0) / 1000);
      } catch (e) {
        if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
          patch = { status: 'cancelled', label: 'megszakítva' };
        } else {
          const d = e?.response?.data?.detail;
          patch = { status: 'failed',
            label: typeof d === 'string' ? d : (e.message || 'Hálózati hiba') };
        }
      }
      abortRef.current = null;
      setItem(idx, patch);
    }
    // Megszakításnál a sorban maradtak jelölése
    if (cancelRef.current && queueRef.current.length) {
      const leftover = queueRef.current.splice(0);
      setItems(prev => prev.map((it, i) =>
        leftover.some(q => q.idx === i) ? { ...it, status: 'cancelled', label: '' } : it));
    }
    cancelRef.current = false;
    runningRef.current = false;
    setBusy(false);
    refresh();
  };

  const process = (fileList) => {
    const all = Array.from(fileList || []);
    const files = all.filter(f =>
      /\.(pdf|png|jpe?g)$/i.test(f.name) || f.type === 'application/pdf');
    if (files.length > 0) {
      posthog?.capture('bulk_upload_started', { file_count: files.length });
    }
    if (!files.length) {
      // Látható visszajelzés akkor is, ha a drop nem hozott használható fájlt
      // (pl. csak-felhőben-lévő OneDrive fájl, vagy nem PDF/kép)
      setNotice(all.length
        ? `A bedobott ${all.length} elem egyike sem PDF/kép – nem indult feldolgozás.`
        : 'A böngésző nem kapott fájlt a droptól (felhőből húzott, le nem töltött ' +
          'fájlnál előfordul) – mentsd le a gépre, vagy kattints és tallózd be.');
      return;
    }
    setNotice(null);
    const start = countRef.current;
    countRef.current += files.length;
    files.forEach((f, i) => { filesRef.current[start + i] = f; });
    queueRef.current.push(...files.map((f, i) => ({ file: f, idx: start + i })));
    setItems(prev => [...prev,
      ...files.map(f => ({ name: f.name, status: 'pending', label: '' }))]);
    runWorker();
  };

  // ── needs_review feloldása ─────────────────────────────────────────────
  const resolveAttach = async (idx, docId) => {
    const file = filesRef.current[idx];
    if (!file) return;
    setItem(idx, { status: 'resolving', label: 'csatolás…' });
    try {
      const r = await documentsApi.attachPdf(docId, file);
      setItem(idx, { status: 'attached',
        label: `#${docId} ${r.supplier_name || ''} ${r.invoice_number || ''}`.trim() });
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setItem(idx, { status: 'needs_review',
        label: 'csatolás hiba: ' + (typeof d === 'string' ? d : (e.message || '')) });
    }
  };

  const resolveCreate = async (idx) => {
    const file = filesRef.current[idx];
    if (!file) return;
    setItem(idx, { status: 'resolving', label: 'létrehozás…' });
    try {
      const r = await accountantApi.bulkUpload(period, file, undefined, { forceCreate: true });
      setItem(idx, { status: r.action === 'created' ? 'created' : (r.action || 'failed'),
        label: `${r.matched_supplier || '?'} ${r.matched_invoice_number || ''} → #${r.doc_id} (${r.accounting_period || 'nincs időszak!'})` });
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setItem(idx, { status: 'needs_review',
        label: 'létrehozás hiba: ' + (typeof d === 'string' ? d : (e.message || '')) });
    }
  };

  const resolveDiscard = (idx) =>
    setItem(idx, { status: 'skipped', label: 'elvetve' });

  const cancel = () => {
    cancelRef.current = true;
    abortRef.current?.abort();
  };

  const clear = () => {
    if (runningRef.current) return;   // futás közben az indexek élnek
    queueRef.current = [];
    countRef.current = 0;
    durationsRef.current = [];
    filesRef.current = {};
    setItems([]);
    setNotice(null);
  };

  // Auto-bezárás: ha a köteg teljesen lezárult (nincs nyitott státuszú sor: sem
  // hiba, sem várakozó döntés), a panel pár másodperc múlva magától eltűnik.
  // needs_review/hiba esetén nyitva marad, hogy a felhasználó tudjon dönteni.
  useEffect(() => {
    if (busy || !items.length) return;
    if (items.some(it => OPEN_STATUSES.includes(it.status))) return;
    const t = setTimeout(() => {
      if (runningRef.current) return;   // időközben új feltöltés indult
      queueRef.current = [];
      countRef.current = 0;
      durationsRef.current = [];
      filesRef.current = {};
      setItems([]);
      setNotice(null);
    }, 5000);
    return () => clearTimeout(t);
  }, [busy, items]);

  // Folyamat-összegzés a státusz panelhez (a nyitott státuszúak nem készek)
  const total = items.length;
  const done = items.filter(it => !OPEN_STATUSES.includes(it.status)).length;
  const remaining = total - done;
  const avg = durationsRef.current.length
    ? durationsRef.current.reduce((a, b) => a + b, 0) / durationsRef.current.length
    : DEFAULT_FILE_SECONDS;
  const progress = total ? {
    total, done, remaining,
    percent: Math.round((done / total) * 100),
    etaSeconds: Math.round(remaining * avg),
    currentName: items.find(it => it.status === 'working')?.name || null,
  } : null;

  return { process, busy, items, notice, progress, cancel, clear,
           resolveAttach, resolveCreate, resolveDiscard };
}

function etaLabel(sec) {
  if (sec <= 0) return '';
  if (sec < 60) return '~1 percen belül';
  return `~${Math.round(sec / 60)} perc hátra`;
}

function fmtAmount(v, ccy) {
  if (v === null || v === undefined) return '';
  return `${huFmt(v)} ${ccy || ''}`.trim();
}

// Egy needs_review sor alatti döntő-panel: kiolvasott adatok + jelölt-gombok
function ReviewPicker({ idx, item, onAttach, onCreate, onDiscard }) {
  const ex = item.extracted || {};
  const cands = item.candidates || [];
  const wrap = {
    margin: '2px 0 6px 22px', padding: '6px 10px',
    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', gap: 6,
    fontSize: '0.76rem',
  };
  const candBtn = {
    textAlign: 'left', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '5px 9px', cursor: 'pointer',
    color: 'var(--text)', fontSize: '0.76rem',
  };
  return (
    <div style={wrap}>
      <div style={{ color: 'var(--text-muted)' }}>
        Kiolvasva:{' '}
        <strong>{ex.supplier_name || '— nincs szállító —'}</strong>
        {ex.invoice_number ? ` · ${ex.invoice_number}` : ''}
        {ex.gross_amount != null ? ` · ${fmtAmount(ex.gross_amount, ex.currency)}` : ''}
        {ex.issue_date ? ` · ${ex.issue_date}` : ''}
        {ex.accounting_period ? ` · időszak: ${ex.accounting_period}` : ''}
      </div>

      {cands.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ color: 'var(--text-dim)' }}>Csatold egy meglévő bizonylathoz:</span>
          {cands.map(c => (
            <button key={c.id} style={candBtn} onClick={() => onAttach(idx, c.id)}>
              <strong>#{c.id}</strong> {c.supplier_name || '?'}{' '}
              {c.invoice_number ? `· ${c.invoice_number} ` : ''}
              · {fmtAmount(c.gross_amount, c.currency)}
              {c.accounting_period ? ` · ${c.accounting_period}` : ''}
              {c.match_reasons?.length
                ? <span style={{ color: 'var(--text-dim)' }}> · egyezik: {c.match_reasons.join(', ')}</span>
                : null}
            </button>
          ))}
        </div>
      ) : (
        <span style={{ color: 'var(--text-dim)' }}>
          Nincs illő, PDF nélküli meglévő bizonylat.
        </span>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
        <Btn size="sm" onClick={() => onCreate(idx)}>Mégis új bizonylat</Btn>
        <button onClick={() => onDiscard(idx)} style={{
          background: 'none', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '3px 10px',
          color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.74rem' }}>
          Elvet
        </button>
      </div>
    </div>
  );
}

export function BulkResults({ items, notice, progress, busy, onCancel, onClear,
                              onResolveAttach, onResolveCreate, onResolveDiscard }) {
  const [showDetails, setShowDetails] = useState(true);
  if (!items.length && !notice) return null;

  const boxS = {
    background:'var(--bg-card)', border:'1px solid var(--border)',
    borderRadius:'var(--radius-lg)', padding:'0.7rem 1rem', marginBottom:'1rem',
    display:'flex', flexDirection:'column', gap:6,
  };

  if (!items.length) {
    return (
      <div style={boxS}>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.8rem'}}>
          <Badge variant="warning">Figyelem</Badge>
          <span>{notice}</span>
          <button onClick={onClear} style={{marginLeft:'auto',background:'none',border:'none',
            color:'var(--text-dim)',cursor:'pointer',fontSize:'0.72rem'}}>✕</button>
        </div>
      </div>
    );
  }

  const needsAction = items.some(it => it.status === 'needs_review');

  return (
    <div style={boxS}>
      {/* Fejléc: folyamatjelző + ETA + cancel */}
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        {busy && <Spinner size={14}/>}
        <span style={{fontSize:'0.82rem',fontWeight:600}}>
          Feltöltés: {progress.done}/{progress.total} kész
        </span>
        {needsAction && (
          <Badge variant="warning">Döntésre vár</Badge>
        )}
        {busy && progress.etaSeconds > 0 && (
          <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>
            {etaLabel(progress.etaSeconds)}
          </span>
        )}
        {busy && progress.currentName && (
          <span style={{fontSize:'0.72rem',color:'var(--text-muted)',fontFamily:'var(--mono)',
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:280}}>
            most: {progress.currentName}
          </span>
        )}
        <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
          <button onClick={()=>setShowDetails(s=>!s)}
            style={{background:'none',border:'none',color:'var(--primary)',
              cursor:'pointer',fontSize:'0.74rem'}}>
            {showDetails ? 'Részletek elrejtése' : 'Részletek mutatása'}
          </button>
          {busy && (
            <Btn size="sm" variant="danger" onClick={onCancel}>Megszakítás</Btn>
          )}
          {!busy && (
            <button onClick={onClear} style={{background:'none',
              border:'1px solid var(--border)', borderRadius:'var(--radius)',
              padding:'2px 10px', color:'var(--text)', cursor:'pointer',
              fontSize:'0.72rem'}}>
              Bezárás
            </button>
          )}
        </div>
      </div>

      {/* Folyamatjelző sáv */}
      <div style={{height:6,background:'rgba(255,255,255,0.07)',borderRadius:99,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${progress.percent}%`,
          background: busy ? 'var(--primary)' : 'var(--success-strong)',
          borderRadius:99, transition:'width 0.4s'}}/>
      </div>

      {/* Részletes lista */}
      {showDetails && (
        <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:360,overflowY:'auto'}}>
          {items.map((it, i) => {
            const [variant, text] = ACTION_BADGE[it.status] || ACTION_BADGE.failed;
            return (
              <div key={i} style={{display:'flex',flexDirection:'column',gap:2}}>
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.78rem'}}>
                  {(it.status === 'working' || it.status === 'resolving') && <Spinner size={12}/>}
                  <Badge variant={variant}>{text}</Badge>
                  <span style={{fontFamily:'var(--mono)'}}>{it.name}</span>
                  <span style={{color:'var(--text-muted)'}}>{it.label}</span>
                </div>
                {it.status === 'needs_review' && (
                  <ReviewPicker idx={i} item={it}
                    onAttach={onResolveAttach} onCreate={onResolveCreate}
                    onDiscard={onResolveDiscard}/>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
